import type { CSSProperties } from 'react';
import type { Tag } from '../../graphql/types';
import { readableTextColor, FALLBACK_TAG_COLOR } from '../../lib/readableTextColor';
import './TagBadge.css';

export interface TagBadgeProps {
  tag: Tag;
  onRemove?: (tagId: string) => void;
  /**
   * Display name of the tag's Tag Group (Tag Category), shown alongside the
   * tag name in the hover tooltip (issue #744). Optional — callers that
   * haven't loaded category data omit it and the tooltip degrades to the
   * pre-#744 `description || name` behavior. Callers that have category data
   * should resolve "Uncategorized" for an unmatched `categoryId` themselves
   * (see `resolveTagCategoryName` in `lib/tagCategoryName.ts`) so the fallback
   * is explicit rather than silently omitted.
   */
  categoryName?: string;
}

/**
 * Inline style with the `--tag-fg` custom property consumed by
 * `TagBadge.css`'s `color: var(--tag-fg)` rule. `CSSProperties` has no index
 * signature for arbitrary custom properties, so this narrow extension is the
 * typed (non-`any`) way to set one.
 */
interface TagBadgeStyle extends CSSProperties {
  '--tag-fg'?: string;
}

/** Builds the hover tooltip text: `<name> - <group>` (+ description line) when a group is supplied, else the pre-#744 `description || name`. */
function buildTooltip(tag: Tag, categoryName?: string): string {
  if (!categoryName) {
    return tag.description || tag.name;
  }
  const header = `${tag.name} - ${categoryName}`;
  return tag.description ? `${header}\n${tag.description}` : header;
}

export function TagBadge({ tag, onRemove, categoryName }: TagBadgeProps): JSX.Element {
  const color = tag.color || FALLBACK_TAG_COLOR;
  const foreground = readableTextColor(color);
  const style: TagBadgeStyle = { backgroundColor: color, '--tag-fg': foreground };

  return (
    <span className="tag-badge" style={style} title={buildTooltip(tag, categoryName)}>
      {tag.name}
      {onRemove && (
        <button
          type="button"
          className="tag-badge__remove"
          aria-label={`Remove ${tag.name}`}
          onClick={() => onRemove(tag.entityId)}
        >
          ×
        </button>
      )}
    </span>
  );
}
